if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
      }

      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.isThumbnailSlider = this.dataset.desktopLayout === 'thumbnail_slider';

        if (!this.elements.thumbnails && !this.isThumbnailSlider) return;

        if (this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]:not([data-clone])').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        if (this.isThumbnailSlider) {
          const activeMedia =
            this.elements.viewer.querySelector('.product__media-item.is-active') ||
            this.elements.viewer.querySelector('.product__media-item');
          if (activeMedia) this.updateAdjacentMedia(activeMedia);
          this._onLayoutChange = () => {
            const active = this.elements.viewer.querySelector('.product__media-item.is-active');
            if (active) this.updateAdjacentMedia(active);
          };
          this.mql.addEventListener('change', this._onLayoutChange);
        }
      }

      disconnectedCallback() {
        if (this._onLayoutChange) {
          this.mql.removeEventListener('change', this._onLayoutChange);
        }
      }

      onSlideChanged(event) {
        if (this.isThumbnailSlider) {
          this.updateAdjacentMedia(event.detail.currentElement);
        }
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]:not([data-clone])`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]:not([data-clone])`) ||
          this.elements.viewer.querySelector('[data-media-id]:not([data-clone])');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]:not([data-clone])').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]:not([data-clone])`
            );
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            if (typeof this.elements.viewer.scrollToRealSlide === 'function') {
              this.elements.viewer.scrollToRealSlide(activeMedia);
            } else {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (this.isThumbnailSlider) {
          this.updateAdjacentMedia(activeMedia);
        }

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]:not([data-clone])`
        );
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      updateAdjacentMedia(activeMedia) {
        const allMedia = [...this.elements.viewer.querySelectorAll('[data-media-id]:not([data-clone])')];
        allMedia.forEach((element) => {
          element.classList.remove('is-adjacent', 'is-preview');
        });

        if (!activeMedia || allMedia.length <= 1) return;

        const activeIndex = allMedia.indexOf(activeMedia);
        const nextIndex = (activeIndex + 1) % allMedia.length;
        const nextNextIndex = (activeIndex + 2) % allMedia.length;

        if (this.mql.matches) {
          allMedia[nextIndex].classList.add('is-adjacent');
        } else {
          allMedia[nextIndex].classList.add('is-preview');
          if (allMedia.length > 2 && nextNextIndex !== activeIndex) {
            allMedia[nextNextIndex].classList.add('is-preview');
          }
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('[data-target]:not([data-clone]) button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        if (typeof this.elements.thumbnails.scrollToRealSlide === 'function') {
          this.elements.thumbnails.scrollToRealSlide(thumbnail);
        } else {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.querySelectorAll('[id^="Slide-"]:not([data-clone])').forEach((slide) => {
          slide.setAttribute('role', 'presentation');
        });
      }
    }
  );
}
